import { useMemo } from "react";
import { Modal } from "antd";
import { useSelector } from "react-redux";
import dayjsTZ from "../../../utils/dayjs";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import { getDayTypeTranslations } from "../../Reports/processFactory/approvals/approvals";
import {
  getDayLeaveItems,
  isLeaveOrHolidayType,
  getAbsenceDayTypes,
  splitShiftIntoBands,
} from "./shiftBands";
import "./ClockInDetailModal.scss";

const formatHours = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(2) : "0.00";

const StatusChip = ({ approved }) => {
  if (approved === true) {
    return <span className="cidm__chip cidm__chip--ok">Aprovuar</span>;
  }
  if (approved === false) {
    return <span className="cidm__chip cidm__chip--warn">Pa Aprovuar</span>;
  }
  return <span className="cidm__chip cidm__chip--neutral">Pa Përcaktuar</span>;
};

const LeaveChip = ({ dayType, approved, absenceTypes }) => {
  const isHoliday =
    isLeaveOrHolidayType(dayType, absenceTypes) &&
    String(dayType).toLowerCase().includes("holiday");

  if (isHoliday) {
    return (
      <span className="cidm__chip cidm__chip--holiday">
        {getDayTypeTranslations(dayType)}
      </span>
    );
  }
  if (approved === true) {
    return <span className="cidm__chip cidm__chip--ok">Aprovuar</span>;
  }
  if (approved === false) {
    return <span className="cidm__chip cidm__chip--warn">Pa Aprovuar</span>;
  }
  return (
    <span className="cidm__chip cidm__chip--neutral">
      {getDayTypeTranslations(dayType) || "Leje"}
    </span>
  );
};

const ClockInDetailModal = ({
  open,
  onClose,
  shift,
  leaves = [],
  clockings = [],
  dayTypes = {},
  dayTypeOptions = [],
}) => {
  const { isDarkMode } = useSelector((state) => state.usersList);

  const absenceTypes = useMemo(
    () => getAbsenceDayTypes(dayTypeOptions),
    [dayTypeOptions]
  );

  const bands = useMemo(
    () => splitShiftIntoBands(shift?.clockInDate, shift?.clockOutDate),
    [shift?.clockInDate, shift?.clockOutDate]
  );

  const leaveItems = useMemo(
    () =>
      getDayLeaveItems({
        clockInDate: shift?.clockInDate,
        leaves,
        clockings,
        fieldOptions: dayTypeOptions,
        dayTypeLabels: dayTypes,
      }),
    [shift?.clockInDate, leaves, clockings, dayTypeOptions, dayTypes]
  );

  const bandCards = [
    {
      key: "before",
      className: "cidm__band--before",
      title: "Para 19:00",
      subtitle: "Orë ditore",
      hours: bands.before19,
    },
    {
      key: "mid",
      className: "cidm__band--mid",
      title: "19:00 – 22:00",
      subtitle: "Orë të vona",
      hours: bands.between19and22,
    },
    {
      key: "after",
      className: "cidm__band--after",
      title: "Pas 22:00",
      subtitle: "Orë nate",
      hours: bands.after22,
    },
  ];

  const totalBand = bands.total || 0;

  if (!shift) return null;

  const weekday =
    shift?.weekDay ||
    (shift?.clockInDate
      ? dayjsTZ(shift.clockInDate).locale("sq").format("dddd")
      : "");
  const dayTypeLabel =
    shift?.dayTypeLabel ||
    dayTypes[shift?.dayType] ||
    getDayTypeTranslations(shift?.dayType || "") ||
    shift?.dayType ||
    "—";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      destroyOnHidden
      width={720}
      wrapClassName={`clockin-detail-modal ${isDarkMode ? "darkHeader" : "lightHeader"}`}
      title="Detajet e turnit"
      footer={[
        <MondayButton key="close" className="mondayButtonRed" onClick={onClose}>
          Mbyll
        </MondayButton>,
      ]}
    >
      <div className={`cidm${isDarkMode ? " cidm--dark" : ""}`}>
        <div className="cidm__hero">
          <div className="cidm__date">
            <strong>
              {shift?.clockInDate
                ? dayjsTZ(shift.clockInDate).format("DD/MM/YYYY")
                : "—"}
            </strong>
            <span>{weekday}</span>
            <div
              style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}
            >
              <StatusChip approved={shift?.approved} />
              <span className="cidm__chip cidm__chip--neutral">
                {dayTypeLabel}
              </span>
            </div>
          </div>
          <div className="cidm__times">
            <div className="cidm__time-block">
              <em>Fillimi</em>
              <b>
                {shift?.clockInDate
                  ? dayjsTZ(shift.clockInDate).format("HH:mm")
                  : "—"}
              </b>
            </div>
            <span className="cidm__time-sep">→</span>
            <div className="cidm__time-block">
              <em>Mbarimi</em>
              <b>
                {shift?.clockOutDate
                  ? dayjsTZ(shift.clockOutDate).format("HH:mm")
                  : "—"}
              </b>
            </div>
          </div>
        </div>

        <p className="cidm__section-title">Ndarja e orëve</p>
        <div className="cidm__bands">
          {bandCards.map((band) => {
            const pct = totalBand > 0 ? (band.hours / totalBand) * 100 : 0;
            return (
              <article key={band.key} className={`cidm__band ${band.className}`}>
                <header>
                  <strong>{band.title}</strong>
                  <span>{band.subtitle}</span>
                </header>
                <div className="cidm__band-value">
                  {formatHours(band.hours)}
                  <small>orë</small>
                </div>
                <div className="cidm__band-bar">
                  <i style={{ width: `${pct}%` }} />
                </div>
              </article>
            );
          })}
        </div>

        <p className="cidm__section-title">Përmbledhje</p>
        <div className="cidm__summary">
          <div className="cidm__stat">
            <em>Kohëzgjatja</em>
            <b>{formatHours(shift?.duration)} orë</b>
          </div>
          <div className="cidm__stat">
            <em>Pushimi</em>
            <b>
              {formatHours(shift?.breakHours)} orë
              {shift?.breakPaidLabel && shift.breakPaidLabel !== "—"
                ? ` · ${shift.breakPaidLabel}`
                : ""}
            </b>
          </div>
          <div className="cidm__stat">
            <em>Orët e punës</em>
            <b>{formatHours(shift?.workingHours)} orë</b>
          </div>
          <div className="cidm__stat">
            <em>Orët e paguara</em>
            <b>{formatHours(shift?.paidHours)} orë</b>
          </div>
        </div>

        {leaveItems.length > 0 ? (
          <>
            <p className="cidm__section-title">Leje & festa</p>
            <div className="cidm__leave">
              <ul className="cidm__leave-list">
                {leaveItems.map((item) => (
                  <li key={item.id} className="cidm__leave-item">
                    <div className="cidm__leave-main">
                      <strong>{item.label}</strong>
                      <span>
                        {item.start
                          ? dayjsTZ(item.start).format("DD/MM/YYYY HH:mm")
                          : "—"}
                        {item.end
                          ? ` → ${dayjsTZ(item.end).format("DD/MM/YYYY HH:mm")}`
                          : ""}
                      </span>
                      {item.note ? <span>{item.note}</span> : null}
                    </div>
                    <LeaveChip
                      dayType={item.dayType}
                      approved={item.approved}
                      absenceTypes={absenceTypes}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default ClockInDetailModal;
